// src/components/team-messages/FullPageRail.tsx
import { Hash, MessageSquare, Users, Home, Search, Plus, AtSign } from 'lucide-react';
import { cn } from '@/lib/utils';

export type RailSection = 'channels' | 'dms' | 'groups';

interface FullPageRailProps {
  activeSection: RailSection;
  onSectionChange: (section: RailSection) => void;
  onBack: () => void;
  userInitials: string;
  mentionCount?: number;
  onSearchFocus?: () => void;
  onNewGroup?: () => void;
}

interface RailButtonProps {
  icon: React.ElementType;
  label: string;
  active?: boolean;
  badge?: number;
  onClick?: () => void;
}

function RailButton({ icon: Icon, label, active, badge, onClick }: RailButtonProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={cn(
        'relative w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-150',
        active
          ? 'border-l-2 border-white bg-[#1e1e1e] text-white'
          : 'text-[#5a5a5a] hover:text-[#fafafa] hover:bg-[#1e1e1e]',
      )}
    >
      <Icon className="h-[18px] w-[18px]" />
      {badge != null && badge > 0 && (
        <span className="absolute top-1 right-1 min-w-[14px] h-[14px] rounded-full bg-white text-black text-[8px] font-bold flex items-center justify-center px-0.5 tabular-nums leading-none">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

const SECTION_BUTTONS: { key: RailSection; Icon: React.ElementType; label: string }[] = [
  { key: 'channels', Icon: Hash,          label: 'Channels' },
  { key: 'dms',      Icon: MessageSquare, label: 'Direct Messages' },
  { key: 'groups',   Icon: Users,         label: 'Groups' },
];

export function FullPageRail({
  activeSection,
  onSectionChange,
  onBack,
  userInitials,
  mentionCount = 0,
  onSearchFocus,
  onNewGroup,
}: FullPageRailProps) {
  const initials = userInitials || '?';

  return (
    <div className="w-14 shrink-0 flex flex-col items-center py-2 gap-0.5 overflow-hidden bg-[#0a0a0a] border-r border-[#2e2e2e]">
      {/* Top: user avatar */}
      <div className="mb-2 mt-1">
        <button
          title="Your profile"
          className="w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-bold text-white bg-[#2e2e2e] ring-1 ring-white/20 hover:ring-white/40 transition-all"
        >
          {initials}
        </button>
      </div>

      {/* Divider */}
      <div className="w-7 h-px bg-[#2e2e2e] mb-1" />

      {/* Back to Koredor */}
      <RailButton icon={Home} label="Back to Koredor" onClick={onBack} />

      {/* Section navigation */}
      {SECTION_BUTTONS.map(({ key, Icon, label }) => (
        <RailButton
          key={key}
          icon={Icon}
          label={label}
          active={activeSection === key}
          onClick={() => onSectionChange(key)}
        />
      ))}

      {/* Divider */}
      <div className="w-7 h-px bg-[#2e2e2e] my-1" />

      {/* @ Mentions */}
      <RailButton
        icon={AtSign}
        label="Mentions"
        badge={mentionCount}
      />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Search */}
      <button
        onClick={onSearchFocus}
        title="Search"
        className="w-10 h-10 flex items-center justify-center rounded-xl text-[#5a5a5a] hover:text-[#fafafa] hover:bg-[#1e1e1e] transition-all duration-150"
      >
        <Search className="h-[18px] w-[18px]" />
      </button>

      {/* Plus — new group */}
      <button
        onClick={onNewGroup}
        title="New"
        className="w-10 h-10 flex items-center justify-center rounded-xl text-[#5a5a5a] hover:text-[#fafafa] hover:bg-[#1e1e1e] transition-all duration-150"
      >
        <Plus className="h-[18px] w-[18px]" />
      </button>

      {/* Bottom user avatar */}
      <div className="mt-2 mb-1">
        <button
          title="Your profile"
          className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold text-white bg-[#2e2e2e] ring-1 ring-white/20 hover:ring-white/40 transition-all"
        >
          {initials}
        </button>
      </div>
    </div>
  );
}
